import { workflowStepService } from "main/src/js/services";
import {
  useMutation,
  useQuery,
  useInfiniteQuery,
  type UseQueryOptions,
  type QueryKey
} from "react-query";
import {
  getCentralizedStudioTemplateJSON,
  getCentralizedStudioTemplates
} from "../services/external.services";
import { paginatedParams, queryClient } from "@certa/queries/utils/utils";

import {
  createStudioApp,
  getStudioApp,
  getStudioApps,
  createStudioAppProcess,
  updateStudioAppProcess,
  addStudioProcessToBranch,
  getStudioBranchSavePoints,
  getStudioBranches,
  getStudioProcess,
  getStudioProcessMetaJSONBySavepoint,
  getStudioProcesses,
  updateStudioProcessMetaJSON,
  getStudioProcessesBySavePoint,
  startWorkingOnBranch,
  stopWorkingOnBranch,
  updateDeployableJSON,
  getDeployableJSON,
  lockBranch,
  unlockBranch,
  validateBranch,
  getProcessDeploymentLogs,
  getSavepointDeploymentLogs,
  getStudioPermissions,
  updateBranchSavepoint,
  getUpdateInProgressWF,
  createBranchSavepoint,
  updateStudioApp,
  deleteStudioApp,
  uploadStudioFilesToS3,
  generateSignedURLForS3Files,
  createBranch,
  mergeBranchesV2,
  getStudioBranchSavePoint,
  saveMetaJSONChangesToCF,
  getActivityLogs,
  ecaDryRun,
  updateMegaJSON,
  getMegaJSON,
  getBranchByName,
  getLatestBranchSavepoint
} from "../services/studio.services";
import { useGetAllPages } from "./utility.hook";
import type { PaginatedResultsResponse } from "../types/generic.types";
import type {
  ObjectMetaData,
  ObjectStage,
  SDOSavePointStatus,
  StudioApp,
  StudioBranch,
  StudioBranchSavePoint,
  StudioCentralTemplate,
  StudioProcessBySavePoint,
  TemplateStatusTypes
} from "../types/studio.types";
import type {
  UseInfiniteQueryExtraConfigType,
  UseMutationExtraConfigType,
  UseQueryReturnType
} from "@certa/common/types";
import {
  getStudioDataObjectSchemas,
  getStudioDataObjectsList,
  getStudioDataObjectsVersionsList,
  updateStudioDataObjectSchema,
  createStudioDataObject,
  deleteStudioDataObject,
  deployStudioDataObjectWithPayload,
  deployStudioDataObjectWithoutPayload,
  getSDObjectListLockStatus,
  lockObjectListDraft,
  unlockObjectListDraft
} from "../services/studioDataObjects.services";
import { useUpdateBranchMutation } from "../queries/studio/updateBranch";
import type { ActivityLogData } from "@certa/studio-core";
import { handleMetaJSONSaveError } from "../utils/studioSentry.utils";

export const useCreateStudioPackagesMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createStudioApp, extraConfig);

export const useUpdateStudioPackagesMutation = (
  extraConfig?: UseMutationExtraConfigType
) =>
  useMutation(updateStudioApp, {
    ...extraConfig,
    onSuccess: app => {
      queryClient.setQueryData(["studio-package", app.id], app);
    }
  });

export const useDeleteAppMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deleteStudioApp, extraConfig);

const useStudioPackagesPaginated = (
  config?: UseInfiniteQueryExtraConfigType
) => {
  return useInfiniteQuery<PaginatedResultsResponse<StudioApp>>(
    ["studio-packages"],
    ({ pageParam = 0 }) => getStudioApps(pageParam),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 10, // 10 seconds
      ...paginatedParams,
      onSuccess: data => {
        data?.pages?.forEach(appData => {
          appData.results.forEach(app =>
            queryClient.setQueryData(["studio-package", app.id], app)
          );
        });
      },
      ...config
    }
  );
};

export const useStudioPackages = (config?: UseInfiniteQueryExtraConfigType) => {
  const queryResult = useStudioPackagesPaginated(config);
  return useGetAllPages(queryResult);
};

export const useStudioPackage = (packageId: number) => {
  return useQuery({
    retry: 0,
    enabled: !!packageId,
    // 10 seconds
    staleTime: 1000 * 10,
    queryKey: ["studio-package", packageId],
    queryFn: () => getStudioApp(packageId)
  });
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStudioProcesses = (packageId: number) => {
  return useQuery({
    retry: 0,
    enabled: !!packageId,
    onSuccess: data => {
      data.forEach(process => {
        queryClient.setQueryData(
          ["studio-process", packageId, process.id],
          process
        );
      });
    },
    queryKey: ["studio-processes", packageId],
    queryFn: () => getStudioProcesses(packageId)
  });
};

export const useStudioProcessesMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioProcesses, extraConfig);

export const useStudioProcess = (packageId: number, processId: number) => {
  return useQuery({
    retry: 0,
    enabled: !!packageId && !!processId,
    queryKey: ["studio-process", packageId, processId],
    queryFn: () => getStudioProcess(packageId, processId)
  });
};

export const useCreateAppProcessMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createStudioAppProcess, extraConfig);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useUpdateAppProcessMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateStudioAppProcess, extraConfig);

export const useStudioBranches = (packageId: number) =>
  useGetAllPages<StudioBranch>(
    useInfiniteQuery(
      ["studio-branches-paginated", packageId],
      async ({ pageParam = 0 }) => {
        return getStudioBranches(packageId, pageParam);
      },
      {
        retry: 0,
        enabled: !!packageId,
        ...paginatedParams,
        refetchOnWindowFocus: false
      }
    )
  );

export const useCreateBranch = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(createBranch, extraConfig);

export const useMergeBranchesV2 = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(mergeBranchesV2, extraConfig);

// TODO: Pagination is not handled here and it'll fetch whatever is the default limit set
// within the service. Currently we're only using the mutation to pick the first branch
// when an app is created, to set that branch as "WIP" so user can directly start working on it
// so it's not an issue with pagination at this point. So be careful if you're using this
// for any use-case where all branches are expected in response
export const useGetStudioBranchesMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioBranches, extraConfig);

export const useStudioBranchSavePoints = (branchId: number) =>
  useGetAllPages<StudioBranchSavePoint>(
    useInfiniteQuery<PaginatedResultsResponse<StudioBranchSavePoint>>(
      ["studio-branch-savepoints-paginated", branchId],
      async ({ pageParam = 0 }) => {
        return getStudioBranchSavePoints(branchId, pageParam);
      },
      {
        retry: 0,
        enabled: !!branchId,
        ...paginatedParams,
        refetchOnWindowFocus: false
      }
    )
  );

export const useStudioProcessesBySavePoint = (
  savepointId: number,
  options?: UseQueryOptions<
    UseQueryReturnType<typeof getStudioProcessesBySavePoint>
  >
) => {
  return useQuery<StudioProcessBySavePoint[]>({
    retry: 0,
    enabled: !!savepointId,
    // 5 seconds
    staleTime: 5 * 1000,
    ...options,
    queryKey: ["studio-processes-by-savepoint", savepointId] as QueryKey,
    queryFn: () => getStudioProcessesBySavePoint(savepointId)
  });
};

export const useStudioSavepointLogs = (savepointId: number) => {
  return useQuery({
    retry: 0,
    enabled: !!savepointId,
    // 5 seconds
    cacheTime: 5 * 1000,
    queryKey: ["studio-savepoint-logs", savepointId],
    queryFn: () => getSavepointDeploymentLogs(savepointId)
  });
};

export const useStudioProcessLogs = (branchLogId: string) => {
  return useQuery({
    retry: 0,
    // 5 seconds
    cacheTime: 5 * 1000,
    enabled: !!branchLogId,
    queryKey: ["studio-process-logs", branchLogId],
    queryFn: () => getProcessDeploymentLogs(branchLogId)
  });
};

export const useStudioBranchSavepoint = (branchSavepointId: number) => {
  return useQuery({
    retry: 0,
    enabled: !!branchSavepointId,
    // 10 seconds
    staleTime: 1000 * 10,
    queryKey: ["studio-branchsavepoint-id", branchSavepointId],
    queryFn: () => getStudioBranchSavePoint(branchSavepointId)
  });
};

/**
 *
 * @param branchLogIds - array of branch log ids
 * Example: ["xxx", "yyy", "zzz"]
 *
 * @returns
 * Array consists of an array of process logs for a branch-log-id.
 * Example:
 * [
 * [..processesLog for Branch Log Id-xxx],
 * [..processesLog for Branch Log Id-yyy],
 * [..processesLog for Branch Log Id-zzz],
 * ]
 */
export const useStudioProcessesLogsGroupedByBranchLogId = (
  branchLogIds: string[]
) => {
  return useQuery({
    retry: 0,
    // 5 seconds
    cacheTime: 5 * 1000,
    enabled: !!branchLogIds.length,
    queryKey: [
      "studio-process-logs-array-grouped-by-branchlog-id",
      `${branchLogIds}`
    ],

    queryFn: async () => {
      return await Promise.all(
        branchLogIds.map(branchLogId => getProcessDeploymentLogs(branchLogId))
      );
    }
  });
};

export const useAddStudioProcessToBranchMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(addStudioProcessToBranch, extraConfig);

export const useStudioProcessMetaJSONBySavepoint = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioProcessMetaJSONBySavepoint, extraConfig);

export const useUpdateStudioProcessMetaJSON = (
  extraConfig?: UseMutationExtraConfigType
) =>
  useMutation(updateStudioProcessMetaJSON, {
    ...extraConfig,
    onError: async (error, vars, context) => {
      handleMetaJSONSaveError({
        error,
        vars,
        context
      });
      extraConfig?.onError?.(error, vars, context);
    }
  });

export const useGetStudioBranchSavePointsMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioBranchSavePoints, extraConfig);

export const useGetStudioProcessesBySavePointMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioProcessesBySavePoint, extraConfig);

export const useStartWorkingOnBranchMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(startWorkingOnBranch, extraConfig);

export const useStopWorkingOnBranchMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(stopWorkingOnBranch, extraConfig);

export const useUpdateDeployableJSON = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateDeployableJSON, extraConfig);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useGetDeployableJSON = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(getDeployableJSON, extraConfig);

export const useLockBranch = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(lockBranch, extraConfig);

export const useUnlockBranch = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(unlockBranch, extraConfig);

export const useValidateBranch = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(validateBranch, extraConfig);

export const usePushMetadata = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(workflowStepService.saveField, extraConfig);

export const useGetUpdateInProgressWFMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getUpdateInProgressWF, extraConfig);

export const useCreateBranchSavepoint = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createBranchSavepoint, extraConfig);

const useStudioPermissionsQueryConfig = {
  retry: 0,
  // 1hr
  staleTime: 3600 * 1000,
  queryKey: ["studio-permissions"],
  queryFn: getStudioPermissions
};

export const useStudioPermissions = () => {
  return useQuery(useStudioPermissionsQueryConfig);
};

export const prefetchStudioPermissions = () => {
  queryClient.prefetchQuery(useStudioPermissionsQueryConfig);
};

export const invalidateStudioBranches = (packageId: number) => {
  queryClient.invalidateQueries(["studio-branches-paginated", packageId]);
};

export const useUpdateBranchSavepointMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateBranchSavepoint, extraConfig);

const useGetCentralStudioTemplatesPaginated = (
  templateState?: TemplateStatusTypes,
  config?: UseInfiniteQueryExtraConfigType
) =>
  useInfiniteQuery(
    [`central-studio-templates-${templateState}`],
    ({ pageParam = 1 }) =>
      getCentralizedStudioTemplates(pageParam, templateState),
    {
      getNextPageParam: lastPage => {
        if (lastPage.next) {
          const urlParams = new URLSearchParams(lastPage.next);
          const nextPage = urlParams.get("page");
          return nextPage;
        }
        return false;
      },
      enabled: true,
      retry: 0,
      cacheTime: Infinity,
      staleTime: Infinity,
      ...config
    }
  );

export const useGetCentralStudioTemplates = (
  templateState?: TemplateStatusTypes,
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useGetCentralStudioTemplatesPaginated(
    templateState,
    config
  );
  return useGetAllPages<StudioCentralTemplate>(queryResult);
};

export const useCentralStudioTemplateJSON = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getCentralizedStudioTemplateJSON, extraConfig);

export const useUploadStudioFilesToS3 = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(uploadStudioFilesToS3, extraConfig);

export const useGenerateSignedURLForS3Files = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(generateSignedURLForS3Files, extraConfig);

export const useSaveMetaJSONChangesToCFMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(saveMetaJSONChangesToCF, extraConfig);

// Standard Data Objects Studio Hooks

export const useCreateDataObjectMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(createStudioDataObject, extraConfig);

const useStudioDataObjectsPaginated = (
  stage: ObjectStage,
  config?: UseInfiniteQueryExtraConfigType
) => {
  return useInfiniteQuery<PaginatedResultsResponse<ObjectMetaData>>(
    ["studio-data-objects-list", stage],
    ({ pageParam = 0 }) =>
      getStudioDataObjectsList({
        stage,
        offset: pageParam
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 10, // 10 seconds
      ...paginatedParams,
      ...config
    }
  );
};

export const useStudioDataObjects = (
  stage: ObjectStage,
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useStudioDataObjectsPaginated(stage, config);
  return useGetAllPages(queryResult);
};

const useStudioDataObjectsVersionsList = (
  stages?: ObjectStage[],
  config?: UseInfiniteQueryExtraConfigType
) =>
  useQuery<SDOSavePointStatus[]>({
    queryKey: ["studio-data-objects-versions-list", stages],
    queryFn: () => getStudioDataObjectsVersionsList({ stages })
  });

export const useStudioDataObjectsVersions = (
  stages?: ObjectStage[],
  config?: UseInfiniteQueryExtraConfigType
) => {
  const queryResult = useStudioDataObjectsVersionsList(stages, config);
  return queryResult;
};

export const useDeployWIPStudioDataObjectMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deployStudioDataObjectWithPayload, extraConfig);

export const useDeployPreviouslyFailedRFTStudioDataObjectMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deployStudioDataObjectWithoutPayload, extraConfig);

export const useDeployRFTStudioDataObjectMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deployStudioDataObjectWithoutPayload, extraConfig);

export const useDeleteStudioDataObjectMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(deleteStudioDataObject, extraConfig);

export const useGetStudioObjectListMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioDataObjectsList, extraConfig);

export const useGetStudioDataObjectSchemasMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getStudioDataObjectSchemas, extraConfig);

export const useUpdateStudioDataObjectSchemaMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateStudioDataObjectSchema, extraConfig);

const useStudioObjectListLockStatusQueryConfig = {
  queryKey: ["studio-object-list-lock-status"],
  queryFn: getSDObjectListLockStatus
};
export const useStudioObjectListLockStatus = () => {
  return useQuery(useStudioObjectListLockStatusQueryConfig);
};

export const prefetchStudioObjectListLockStatus = () => {
  queryClient.prefetchQuery(useStudioObjectListLockStatusQueryConfig);
};

export const useStudioObjectListLock = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(lockObjectListDraft, extraConfig);

export const useStudioObjectListUnlock = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(unlockObjectListDraft, extraConfig);

// activity-logs
export const useStudioActivityLogs = ({
  appId,
  branchId,
  processId,
  branchSavepointId,
  processSavepointId,
  limit = 10
}: {
  appId: number;
  branchId: number;
  branchSavepointId: number;
  processId: number;
  processSavepointId: number;
  limit?: number;
}) => {
  return useInfiniteQuery<{
    logs: ActivityLogData[];
    cursor: string;
    hasMore: boolean;
  }>({
    queryKey: [
      "studio-activity-logs",
      appId,
      branchId,
      branchSavepointId,
      processId,
      processSavepointId
    ],
    queryFn: ({ pageParam }) =>
      getActivityLogs({
        appId,
        branchId,
        branchSavepointId,
        processId,
        processSavepointId,
        limit: 20,
        lastLogKey: pageParam
      }),
    getNextPageParam: lastPage =>
      lastPage.hasMore ? lastPage.cursor : undefined
  });
};

export const useECARunMutation = (extraConfig?: UseMutationExtraConfigType) =>
  useMutation(ecaDryRun, {
    ...extraConfig,
    onSuccess: (data, variables, context) => {
      extraConfig?.onSuccess?.(data, variables, context);
    }
  });

export const useUpdateMegaJSONMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(updateMegaJSON, extraConfig);

export const useGetMegaJSONMutation = (
  extraConfig?: UseMutationExtraConfigType
) => useMutation(getMegaJSON, extraConfig);

export const useBranchByName = ({
  packageId,
  branchName,
  enabled = true
}: {
  packageId: number;
  branchName: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["studio-branch-by-name", packageId, branchName],
    queryFn: () => getBranchByName({ packageId, branchName }),
    enabled: enabled && !!packageId && !!branchName,
    retry: 0,
    staleTime: 1000 * 30 // 30 seconds
  });
};

export const useLatestBranchSavepoint = ({
  branchId,
  enabled = true
}: {
  branchId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: ["studio-latest-branch-savepoint", branchId],
    queryFn: () => getLatestBranchSavepoint(branchId),
    enabled: enabled && !!branchId,
    retry: 0,
    staleTime: 1000 * 30 // 30 seconds
  });
};

export { useUpdateBranchMutation };
